import {
  AlertTriangle,
  Bot,
  Brain,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardEdit,
  Copy,
  Database,
  ExternalLink,
  FileIcon,
  FileSearch,
  FileSymlink,
  FileText,
  FolderInput,
  FolderOpen,
  Loader2,
  RefreshCw,
  UserPlus,
  UsersRound,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { WorkspaceDemandProgressBar } from '@/components/ui/DemandGenProgress';
import { cn } from '@/lib/utils';
import {
  blobsApiClient,
  useGetActiveBulkReSortOperationsQuery,
} from '@/services/api/blobService';
import {
  useGetMatterWorkflowsQuery,
  WorkflowInfo,
} from '@/services/api/workflowService';
import { buildTemporalWorkflowUrl } from '@/utils/temporalUtils';

import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

// Compact copy button with visual feedback
const CopyButton: React.FC<{ text: string; label: string }> = ({
  text,
  label,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Silently fail if clipboard is not available
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors',
        copied && 'text-green-600 dark:text-green-400',
      )}
      aria-label={`Copy ${label}`}
      title={copied ? 'Copied!' : `Copy ${label}`}
    >
      {copied ? (
        <Check className="h-3 w-3" />
      ) : (
        <Copy className="h-3 w-3 text-zinc-400" />
      )}
    </button>
  );
};

interface WorkflowIndicatorProps {
  matterId: string;
}

// Enhanced workflow metadata focused on clear descriptions
interface WorkflowMetadata {
  friendlyName: string;
  description: string;
  icon: React.ReactNode;
}

const workflowMetadata: Record<string, WorkflowMetadata> = {
  ArtifactDigestWorkflow: {
    friendlyName: 'Creating Smart Summary',
    description: 'Extracting facts, dates, and key details from document',
    icon: <FileSearch className="h-4 w-4" />,
  },
  ZipFileUnzipWorkflow: {
    friendlyName: 'Unpacking Archive',
    description: 'Extracting files and organizing into folders',
    icon: <FolderOpen className="h-4 w-4" />,
  },
  PostArtifactCreationWorkflow: {
    friendlyName: 'Setting Up New Document',
    description: 'Preparing document for AI analysis',
    icon: <FileText className="h-4 w-4" />,
  },
  AutoSortBlobWorkflow: {
    friendlyName: 'Smart File Sorting',
    description: 'Moving file to appropriate folder based on content',
    icon: <FolderInput className="h-4 w-4" />,
  },
  BulkReSortBlobsWorkflow: {
    friendlyName: 'Bulk File Re-Sorting',
    description: 'Re-sorting multiple files based on firm rules',
    icon: <FolderInput className="h-4 w-4" />,
  },
  BlobTransformWorkflow: {
    friendlyName: 'Making Document AI-Ready',
    description: 'Converting file for search and analysis',
    icon: <FileSymlink className="h-4 w-4" />,
  },
  DraftProviderWorkflow: {
    friendlyName: 'Researching Medical Provider',
    description: 'Looking up provider contact details and information',
    icon: <ClipboardEdit className="h-4 w-4" />,
  },
  MedicalProviderMergeWorkflow: {
    friendlyName: 'Organizing Provider Records',
    description: 'Cleaning up duplicate provider information',
    icon: <UsersRound className="h-4 w-4" />,
  },
  ProcessSingleProviderWorkflow: {
    friendlyName: 'Adding Medical Provider',
    description: 'Creating provider record with contact info and folders',
    icon: <UserPlus className="h-4 w-4" />,
  },
  ExtractCoverageMentionsWorkflow: {
    friendlyName: 'Finding Insurance Details',
    description: 'Scanning for policy information and coverage limits',
    icon: <FileSearch className="h-4 w-4" />,
  },
  ExtractIncidentMentionsWorkflow: {
    friendlyName: 'Building Incident Timeline',
    description: 'Extracting accident dates, locations, and details',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  MatterDeliverablesWorkflow: {
    friendlyName: 'Checking Document Requirements',
    description: 'Reviewing documents for required deliverables',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  ArtifactDeliverablesWorkflow: {
    friendlyName: 'Checking If Document Is Required',
    description: 'Determining if document satisfies deliverable requirements',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  // ---- Additional workflows for comprehensive coverage ----
  AgentOrchestratorWorkflow: {
    friendlyName: 'Coordinating AI Agents',
    description: 'Orchestrating multiple AI agents for complex task',
    icon: <Bot className="h-4 w-4" />,
  },
  AirtableSyncWorkflow: {
    friendlyName: 'Syncing Airtable Records',
    description: 'Synchronizing data with Airtable',
    icon: <Database className="h-4 w-4" />,
  },
  AirtableWebhookRefreshWorkflow: {
    friendlyName: 'Refreshing Airtable Webhooks',
    description: 'Updating Airtable webhook connections',
    icon: <RefreshCw className="h-4 w-4" />,
  },
  DemandGenerationWorkflow: {
    friendlyName: 'Drafting Demand Letter',
    description: 'Generating demand letter from case facts',
    icon: <FileText className="h-4 w-4" />,
  },
  DemandGenerationFromTemplateWorkflow: {
    friendlyName: 'Drafting Demand Letter',
    description: 'Generating demand letter from template',
    icon: <FileText className="h-4 w-4" />,
  },
  ProofreadingWorkflow: {
    friendlyName: 'Proofreading Document',
    description: 'Checking against firm proofreading rules',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  DocumentSectionsWorkflow: {
    friendlyName: 'Summarizing Document Sections',
    description: 'Identifying and summarizing document sections',
    icon: <FileSearch className="h-4 w-4" />,
  },
  EventPublisherWorkflow: {
    friendlyName: 'Publishing Case Events',
    description: 'Broadcasting events to system components',
    icon: <Bot className="h-4 w-4" />,
  },
  SubscriberWorkflow: {
    friendlyName: 'Listening for Case Events',
    description: 'Waiting for events to trigger actions',
    icon: <Bot className="h-4 w-4" />,
  },
  MarkdownTransformWorkflow: {
    friendlyName: 'Converting to Markdown',
    description: 'Converting document to markdown format',
    icon: <FileSymlink className="h-4 w-4" />,
  },
  BlockPreviewGenerationWorkflow: {
    friendlyName: 'Generating Page Previews',
    description: 'Creating visual previews of document pages',
    icon: <FileIcon className="h-4 w-4" />,
  },
  EnhancedBlandCallWorkflow: {
    friendlyName: 'Summarizing Phone Call',
    description: 'Transcribing and analyzing phone call',
    icon: <FileText className="h-4 w-4" />,
  },
  PoliceReportLookupWorkflow: {
    friendlyName: 'Retrieving Police Report',
    description: 'Looking up police report',
    icon: <FileSearch className="h-4 w-4" />,
  },
  TranscribeWorkflow: {
    friendlyName: 'Transcribing Audio',
    description: 'Converting audio to searchable text',
    icon: <FileText className="h-4 w-4" />,
  },
  BlandCallWorkflow: {
    friendlyName: 'Processing Phone Call',
    description: 'Logging call and extracting information',
    icon: <FileText className="h-4 w-4" />,
  },
  BlandProcessWebhookWorkflow: {
    friendlyName: 'Handling Call Webhook',
    description: 'Processing call system webhook update',
    icon: <RefreshCw className="h-4 w-4" />,
  },
  MatterAssistantWorkflow: {
    friendlyName: 'Syncing Matter Files',
    description: 'Syncing and refreshing matter files',
    icon: <FolderInput className="h-4 w-4" />,
  },
  ApplyCoverageRecommendationWorkflow: {
    friendlyName: 'Applying Coverage Updates',
    description: 'Updating coverage records from AI recommendations',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  CoverageEnrichmentWorkflow: {
    friendlyName: 'Enriching Coverage Data',
    description: 'Adding missing details to coverage records',
    icon: <Database className="h-4 w-4" />,
  },
  ApplyIncidentConsolidationWorkflow: {
    friendlyName: 'Consolidating Incident Info',
    description: 'Merging duplicate incident mentions',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  MatterCoverageBufferWorkflow: {
    friendlyName: 'Buffering Coverage Mentions',
    description: 'Batching coverage mentions for processing',
    icon: <Database className="h-4 w-4" />,
  },
  MatterIncidentBufferWorkflow: {
    friendlyName: 'Buffering Incident Mentions',
    description: 'Batching incident mentions for processing',
    icon: <Database className="h-4 w-4" />,
  },
  DemandExampleTransformWorkflow: {
    friendlyName: 'Transforming Demand Example',
    description: 'Converting sample demand to structured data',
    icon: <FileSymlink className="h-4 w-4" />,
  },
  DummyWorkflow: {
    friendlyName: 'Testing Workflow',
    description: 'Testing system health',
    icon: <Bot className="h-4 w-4" />,
  },
  MoBrainRiskWorkflow: {
    friendlyName: 'Assessing Risk',
    description: 'Evaluating overtreatment risk with MoBrain AI engine',
    icon: <Brain className="h-4 w-4" />,
  },
};

// Enhanced workflow with metadata
interface EnhancedWorkflow extends WorkflowInfo {
  metadata: WorkflowMetadata;
  displayName: string;
}

const enhanceWorkflowInfo = (workflow: WorkflowInfo): EnhancedWorkflow => {
  const metadata = workflowMetadata[workflow.workflow_type] || {
    friendlyName: workflow.workflow_type.replace(/Workflow$/, ''),
    description: 'Finch is processing...',
    icon: <Loader2 className="h-4 w-4" />,
  };

  let displayName = metadata.friendlyName;

  // Add file context if available
  if (workflow.artifact_id) {
    const shortId = workflow.artifact_id.slice(-8);
    displayName = `${displayName} (File: ${shortId})`;
  }

  return {
    ...workflow,
    metadata,
    displayName,
  };
};

// Group workflows by category
interface GroupedWorkflows {
  [category: string]: {
    workflows: EnhancedWorkflow[];
  };
}

const groupWorkflowsByCategory = (
  workflows: WorkflowInfo[],
): GroupedWorkflows => {
  const grouped: GroupedWorkflows = {};

  workflows.forEach((workflow) => {
    const enhanced = enhanceWorkflowInfo(workflow);
    const category = workflow.category || 'other';

    if (!grouped[category]) {
      grouped[category] = {
        workflows: [],
      };
    }

    grouped[category].workflows.push(enhanced);
  });

  return grouped;
};

const CategoryIcon: React.FC<{ category: string }> = ({ category }) => {
  switch (category) {
    case 'document-processing':
      return <FileText className="h-4 w-4" />;
    case 'data-extraction':
      return <FileSearch className="h-4 w-4" />;
    case 'organization':
      return <FolderInput className="h-4 w-4" />;
    case 'provider-management':
      return <UserPlus className="h-4 w-4" />;
    default:
      return <Bot className="h-4 w-4" />;
  }
};

const categoryDisplayNames: Record<string, string> = {
  'document-processing': 'Document Processing',
  'data-extraction': 'Information Extraction',
  organization: 'File Organization',
  'provider-management': 'Medical Providers',
  other: 'Other Tasks',
};

const categoryDescriptions: Record<string, string> = {
  'document-processing': 'Converting and analyzing documents',
  'data-extraction': 'Finding specific information in documents',
  organization: 'Organizing and sorting files',
  'provider-management': 'Managing medical provider information',
  other: 'General processing tasks',
};

const WorkflowIndicator: React.FC<WorkflowIndicatorProps> = ({ matterId }) => {
  const dispatch = useDispatch();
  const {
    data: workflows,
    isLoading,
    refetch,
  } = useGetMatterWorkflowsQuery(matterId, {
    skip: !matterId,
    pollingInterval: 20000, // Poll every 20 seconds
    skipPollingIfUnfocused: true,
  });

  // Fetch active bulk re-sort operations
  const { data: bulkReSortOperations, refetch: refetchBulkReSort } =
    useGetActiveBulkReSortOperationsQuery(
      { matterId },
      {
        skip: !matterId,
        pollingInterval: 3000, // Poll more frequently for progress updates
        skipPollingIfUnfocused: true,
      },
    );

  // Track previous bulk re-sort state to detect completion
  const prevHadActiveReSort = useRef<boolean>(false);

  // Check if there are any active bulk re-sort operations
  const activeBulkReSort = bulkReSortOperations?.[0]; // Get the first active one
  const hasActiveBulkReSort = activeBulkReSort !== undefined;

  // Detect when bulk re-sort completes and refresh the file list
  useEffect(() => {
    // If we previously had an active operation but now we don't, it completed
    if (prevHadActiveReSort.current && !hasActiveBulkReSort) {
      // Invalidate the blobs cache to refresh the file list
      dispatch(
        blobsApiClient.util.invalidateTags([{ type: 'Blobs', id: matterId }]),
      );
    }
    prevHadActiveReSort.current = hasActiveBulkReSort;
  }, [hasActiveBulkReSort, matterId, dispatch]);

  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handlePopoverTriggerClick = () => {
    refetch();
    refetchBulkReSort();
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    refetch();
    refetchBulkReSort();
  };

  if (isLoading) {
    return <Loader2 className="h-4 w-4 text-zinc-400 animate-spin" />;
  }

  // Show indicator if there are workflows OR active bulk re-sort operations
  const hasWorkflows = workflows && workflows.length > 0;

  if (!hasWorkflows && !hasActiveBulkReSort) {
    return null;
  }

  const groupedWorkflows = groupWorkflowsByCategory(workflows || []);
  const categories = Object.keys(groupedWorkflows);
  const totalWorkflows = (workflows?.length || 0) + (hasActiveBulkReSort ? 1 : 0);

  const hasDemandGen = workflows?.some((w) =>
    [
      'DemandGenerationWorkflow',
      'DemandGenerationFromTemplateWorkflow',
    ].includes(w.workflow_type),
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="ml-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
          aria-label={`${totalWorkflows} active task${totalWorkflows > 1 ? 's' : ''}`}
          onClick={handlePopoverTriggerClick}
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span className="text-xs font-medium">{totalWorkflows}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-0 rounded-xl overflow-hidden shadow-xl border-zinc-200"
        align="start"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 border-b border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-blue-100">
                <Bot className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">
                  Finch is Finching
                </h3>
                <p className="text-xs text-zinc-600 mt-0.5">
                  {totalWorkflows} active task{totalWorkflows > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors"
              aria-label="Refresh workflows"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>

          {hasDemandGen && (
            <div className="mt-3">
              <WorkspaceDemandProgressBar matterId={matterId} inline />
            </div>
          )}

          {/* Bulk Re-Sort Progress */}
          {activeBulkReSort && (
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <FolderInput className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-zinc-900">
                  Bulk File Re-Sorting
                </span>
              </div>
              <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-500 ease-out rounded-full"
                  style={{
                    width: `${activeBulkReSort.total_blobs > 0 ? Math.round((activeBulkReSort.processed_count / activeBulkReSort.total_blobs) * 100) : 0}%`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-600">
                <span>
                  {activeBulkReSort.processed_count}/{activeBulkReSort.total_blobs} files
                </span>
                <div className="flex items-center gap-2">
                  {activeBulkReSort.moved_count > 0 && (
                    <span className="text-blue-600">
                      {activeBulkReSort.moved_count} moved
                    </span>
                  )}
                  {activeBulkReSort.renamed_count > 0 && (
                    <span className="text-purple-600">
                      {activeBulkReSort.renamed_count} renamed
                    </span>
                  )}
                  {activeBulkReSort.failed_count > 0 && (
                    <span className="text-red-600">
                      {activeBulkReSort.failed_count} failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          {categories.map((category) => {
            const group = groupedWorkflows[category];
            const isExpanded = expandedCategories[category];
            const workflowCount = group.workflows.length;

            return (
              <div
                key={category}
                className="border-b border-zinc-100 last:border-b-0"
              >
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="w-full text-left p-4 hover:bg-zinc-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 rounded-md bg-blue-100 text-blue-600">
                        <CategoryIcon category={category} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-zinc-900">
                            {categoryDisplayNames[category] || category}
                          </span>
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-medium">
                            {workflowCount}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-600 mt-0.5">
                          {categoryDescriptions[category] || 'Processing tasks'}
                        </p>
                      </div>
                    </div>
                    <ChevronRight
                      className={`h-4 w-4 text-zinc-400 transition-transform ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                    />
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    {group.workflows.map((workflow) => (
                      <div
                        key={workflow.workflow_id}
                        className="ml-8 p-3 bg-zinc-50 rounded-lg border border-zinc-200"
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 text-blue-600">
                            {workflow.metadata.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1 min-w-0">
                                <CopyButton
                                  text={workflow.workflow_id}
                                  label="workflow ID"
                                />
                                <h4 className="font-medium text-sm text-zinc-900 truncate">
                                  {workflow.displayName}
                                </h4>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                <span className="text-xs text-zinc-600">
                                  Active
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-zinc-600 mb-2 leading-relaxed">
                              {workflow.metadata.description}
                            </p>
                            {workflow.artifact_id && (
                              <div className="flex items-center gap-1 mt-2">
                                <FileIcon className="h-3 w-3 text-zinc-400" />
                                <span className="text-xs font-mono text-zinc-500">
                                  File ID: {workflow.artifact_id.slice(-8)}
                                </span>
                              </div>
                            )}
                            <a
                              href={buildTemporalWorkflowUrl(
                                workflow.workflow_id,
                                workflow.run_id,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-xs text-blue-500 hover:text-blue-700 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-3 w-3" />
                              View in Temporal
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-3 bg-zinc-50 border-t border-zinc-200">
          <div className="flex items-center gap-2 text-xs text-zinc-600">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            <span>
              Files may be moved or renamed while processing. New content will
              appear when tasks complete.
            </span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default WorkflowIndicator;
